import { useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, LayoutGrid, Loader2, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { User } from '@/types';
import type { Board } from '@/types/api/course.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BoardMembershipRole, NormalizedRole } from './types';

type ManageBoardsDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  user: User | null;
  normalizedRole: NormalizedRole | null;
  boards: Board[];
  joinedBoardIds: Set<number>;
  isLoadingBoards: boolean;
  isMovingToBoard: boolean;
  onMoveToBoard: (boardId: number) => void;
};

const getRoleBadgeStyles = (role: BoardMembershipRole) => {
  if (role === 'Head of board') {
    return 'bg-blue-500/10 text-blue-700 dark:text-blue-300';
  }
  if (role === 'Instructor') {
    return 'bg-violet-500/10 text-violet-700 dark:text-violet-300';
  }
  if (role === 'Learner') {
    return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  }
  return 'bg-muted text-muted-foreground';
};

const getBoardMembershipRole = (
  board: Board,
  user: User,
  joinedBoardIds: Set<number>,
  normalizedRole: NormalizedRole | null
): BoardMembershipRole => {
  if (board.head_of_board === user.id) return 'Head of board';
  if (!joinedBoardIds.has(board.id)) return 'Not Joined';
  return normalizedRole === 'learner' ? 'Learner' : 'Instructor';
};

const ManageBoardsDialog = ({
  open,
  onOpenChange,
  user,
  normalizedRole,
  boards,
  joinedBoardIds,
  isLoadingBoards,
  isMovingToBoard,
  onMoveToBoard,
}: ManageBoardsDialogProps) => {
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);

  const sortedBoards = useMemo(
    () => [...boards].sort((a, b) => a.board_name.localeCompare(b.board_name)),
    [boards]
  );

  const selectedBoard = useMemo(
    () => sortedBoards.find((board) => board.id === selectedBoardId) ?? null,
    [sortedBoards, selectedBoardId]
  );

  const selectedRole = useMemo(() => {
    if (!selectedBoard || !user) return 'Not Joined' as BoardMembershipRole;
    return getBoardMembershipRole(selectedBoard, user, joinedBoardIds, normalizedRole);
  }, [selectedBoard, user, joinedBoardIds, normalizedRole]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen && !isMovingToBoard) setSelectedBoardId(null);
      }}
    >
      <DialogContent className="sm:max-w-[760px] p-0 overflow-hidden">
        <DialogHeader className="border-b border-border/50 bg-muted/20 px-6 py-5">
          <DialogTitle className="text-2xl">Manage boards</DialogTitle>
        </DialogHeader>

        <div className="p-6">
          {!selectedBoard ? (
            <>
              {isLoadingBoards ? (
                <div className="flex min-h-48 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedBoards.map((board) => {
                    if (!user) return null;
                    const role = getBoardMembershipRole(
                      board,
                      user,
                      joinedBoardIds,
                      normalizedRole
                    );

                    return (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => setSelectedBoardId(board.id)}
                        className={cn(
                          'flex w-full items-center justify-between rounded-xl border border-border/50 bg-card px-4 py-3 text-left transition-all',
                          'hover:bg-muted/30 hover:border-border'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                            <LayoutGrid className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">
                              {board.board_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {board.teachers} instructors • {board.students} learners
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Badge
                            variant="secondary"
                            className={cn('rounded-full border px-2.5 py-0.5', getRoleBadgeStyles(role))}
                          >
                            {role}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                onClick={() => setSelectedBoardId(null)}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <div className="rounded-2xl border border-border/60 bg-card p-6">
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-semibold tracking-tight">
                      {selectedBoard.board_name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedBoard.description || 'No board description'}
                    </p>
                  </div>
                  <Badge
                    variant="secondary"
                    className={cn('rounded-full border px-2.5 py-0.5', getRoleBadgeStyles(selectedRole))}
                  >
                    {selectedRole}
                  </Badge>
                </div>

                {selectedRole === 'Not Joined' ? (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onMoveToBoard(selectedBoard.id)}
                      disabled={isMovingToBoard}
                      className="rounded-full"
                    >
                      {isMovingToBoard ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Moving...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Move to board
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border/50 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                    This user is already assigned to this board.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManageBoardsDialog;
